<template>
  <el-dialog
    title="🎙️ 麦克风权限开启指引"
    :visible.sync="visible"
    width="900px"
    :close-on-click-modal="false"
    custom-class="microphone-permission-guide-dialog"
    @close="handleClose"
    class="microphoneDialog"
  >
    <div class="guide-container">
      <p class="guide-subtitle">面试录音需要浏览器麦克风权限，请根据您的使用环境参考以下操作</p>

      <!-- 页签栏 -->
      <div class="tab-bar">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="tab-btn"
          :class="{ active: activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- Windows 企微浏览器 -->
      <div v-show="activeTab === 'win-wecom'" class="tab-panel">
        <div class="section">
          <h2>Windows 企微浏览器</h2>
          <p class="desc">
            <strong>第一步：</strong>点击「开始录音」后，会弹出「申请使用 🎤 麦克风」对话框，点击 <strong>「允许」</strong>。
          </p>
          <div class="img-box">
            <img :src="winWecomAllowImg" alt="Windows企微申请使用麦克风" @click="showLightbox(winWecomAllowImg)">
          </div>
          <p class="desc" style="margin-top:18px;">
            <strong>补充：</strong>如果第一步误点了「取消」或未授权，可以点击地址栏左侧的 <strong>🔒 图标</strong>，在弹出的面板中勾选 <strong>麦克风：☑ 允许</strong>，然后点击 <strong>「确定」</strong>，页面会重新加载并获得权限。
          </p>
          <div class="img-box">
            <img :src="winWecomImg" alt="Windows企微浏览器麦克风权限" @click="showLightbox(winWecomImg)">
          </div>
        </div>
      </div>

      <!-- Mac 企微浏览器 -->
      <div v-show="activeTab === 'mac-wecom'" class="tab-panel">
        <div class="section">
          <h2>Mac 企微浏览器（Safari 内核）</h2>
          <p class="desc">
            点击「开始录音」后，系统会弹出 <strong>「允许 "ihire.dhrforce.com" 使用你的麦克风？」</strong> 对话框，点击 <strong>「允许」</strong>。
            <br>如果之前拒绝过，可右键地址栏选择 <strong>「网站设置…」</strong>，将麦克风改为 <strong>「询问」</strong> 或 <strong>「允许」</strong>，然后刷新页面。
          </p>
          <div class="img-box">
            <img :src="macWecomImg" alt="Mac企微浏览器麦克风权限" @click="showLightbox(macWecomImg)">
          </div>
        </div>
      </div>

      <!-- Chrome 浏览器 -->
      <div v-show="activeTab === 'chrome'" class="tab-panel">
        <div class="section">
          <h2>Chrome 浏览器</h2>
          <p class="desc">
            首次录音时浏览器会弹出权限请求，选择 <strong>「访问网站时允许」</strong> 或 <strong>「仅这次访问时允许」</strong>。
            <br>如果之前拒绝过，点击地址栏左侧 <strong>🔒 锁头图标</strong>，找到「麦克风」将其改为 <strong>允许</strong>，或点击 <strong>「重置权限」</strong> 后刷新页面。
          </p>
          <div class="img-box">
            <img :src="chromeImg" alt="Chrome浏览器麦克风权限" @click="showLightbox(chromeImg)">
          </div>
        </div>
      </div>

      <!-- Safari 浏览器 -->
      <div v-show="activeTab === 'safari'" class="tab-panel">
        <div class="section">
          <h2>Safari 浏览器</h2>
          <p class="desc">
            打开 <strong>Safari → 偏好设置（⌘+,）→ 网站 → 麦克风</strong>，在右侧找到对应网站，将权限从「拒绝」改为 <strong>「询问」</strong> 或 <strong>「允许」</strong>，然后刷新页面。
          </p>
          <div class="img-box">
            <img :src="safariImg" alt="Safari浏览器麦克风权限" @click="showLightbox(safariImg)">
          </div>
        </div>
      </div>

      <!-- 腾讯会议 + 面试录音 -->
      <div v-show="activeTab === 'meeting'" class="tab-panel">
        <div class="section">
          <h2>开启腾讯会议时低音量</h2>
          <p class="desc">
            <strong>提示：</strong>点击开始录音后，后台的腾讯会议声音会受影响，很小，要很大声对方才能听到
          </p>
          <p class="desc"><strong>处理方式：</strong></p>
          <div class="img-box">
            <img :src="tencentMeetingImg" alt="腾讯会议音频设置" @click="showLightbox(tencentMeetingImg)">
          </div>
        </div>
      </div>
    </div>

    <!-- 灯箱 -->
    <div v-if="lightboxVisible" class="lightbox" @click="lightboxVisible = false">
      <img :src="lightboxImg" alt="">
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button type="primary" @click="handleClose">我知道了</el-button>
    </span>
  </el-dialog>
</template>

<script>
import winWecomAllowImg from './assets/permission-guide/win-wecom-allow.png'
import winWecomImg from './assets/permission-guide/win-wecom.png'
import macWecomImg from './assets/permission-guide/mac-wecom.png'
import chromeImg from './assets/permission-guide/chrome.png'
import safariImg from './assets/permission-guide/safari.png'
import tencentMeetingImg from './assets/permission-guide/tencent-meeting.png'

export default {
  name: 'MicrophonePermissionGuide',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: this.value,
      activeTab: 'win-wecom',
      tabs: [
        { key: 'win-wecom', label: 'Windows 企微' },
        { key: 'mac-wecom', label: 'Mac 企微' },
        { key: 'chrome', label: 'Chrome' },
        { key: 'safari', label: 'Safari' },
        { key: 'meeting', label: '开启腾讯会议时低音量' }
      ],
      winWecomAllowImg,
      winWecomImg,
      macWecomImg,
      chromeImg,
      safariImg,
      tencentMeetingImg,
      lightboxVisible: false,
      lightboxImg: ''
    }
  },
  watch: {
    value(val) {
      this.visible = val
    },
    visible(val) {
      this.$emit('input', val)
    }
  },
  methods: {
    handleClose() {
      this.visible = false
      this.$emit('close')
    },
    showLightbox(imgSrc) {
      this.lightboxImg = imgSrc
      this.lightboxVisible = true
    }
  }
}
</script>

<style lang="scss" scoped>
.guide-container {
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: #333;
  line-height: 1.7;
}

.guide-subtitle {
  text-align: center;
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
}

/* 页签栏 */
.tab-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  background: #f5f6f8;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.tab-btn {
  flex: 1;
  min-width: 100px;
  padding: 10px 8px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all .2s;
  text-align: center;
  color: #555;

  &:hover {
    border-color: #4285f4;
    color: #4285f4;
  }

  &.active {
    background: #4285f4;
    color: #fff;
    border-color: #4285f4;
  }
}

/* 面板 */
.tab-panel {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section {
  background: #fff;
  border-radius: 10px;
  padding: 24px 28px;

  h2 {
    font-size: 18px;
    color: #1a73e8;
    margin-bottom: 12px;
    border-bottom: 2px solid #e8f0fe;
    padding-bottom: 8px;
  }
}

.desc {
  font-size: 14px;
  color: #555;
  margin-bottom: 14px;

  strong {
    color: #1a73e8;
  }
}

.img-box {
  text-align: center;
  margin: 12px 0 4px;

  img {
    max-width: 100%;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, .08);
    cursor: zoom-in;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.02);
    }
  }
}

/* 灯箱 */
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .85);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: zoom-out;

  img {
    max-width: 94%;
    max-height: 94vh;
    border-radius: 6px;
  }
}

@media (max-width: 600px) {
  .tab-btn {
    min-width: 70px;
    font-size: 12px;
    padding: 8px 4px;
  }

  .section {
    padding: 18px 16px;
  }
}
</style>

<style lang="scss">
.microphone-permission-guide-dialog {
  .el-dialog__header {
    background: linear-gradient(135deg, #1a73e8, #4285f4);
    color: #fff;
    padding: 20px;
    border-radius: 4px 4px 0 0;

    .el-dialog__title {
      color: #fff;
      font-size: 20px;
      font-weight: 500;
    }

    .el-dialog__headerbtn .el-dialog__close {
      color: #fff;
      font-size: 20px;

      &:hover {
        color: #fff;
      }
    }
  }

  .el-dialog__body {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
  }
}
.microphoneDialog{
  z-index:10000 !important;
}
</style>
